import React, { useEffect, useState, useCallback } from "react";
import { ENDPOINTS, authFetch } from "../../api/api";
import "../components/AdminLayout.css";

// ── Reusable toast hook ──────────────────────────────────────────────────────
const useToast = () => {
  const [toast, setToast] = useState(null);
  const show = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };
  return { toast, show };
};

// ── Project form modal ───────────────────────────────────────────────────────
const ProjectModal = ({ project, onClose, onSaved }) => {
  const { toast, show } = useToast();
  const isEdit = !!project?.id;

  const [form, setForm] = useState({
    title:       project?.title       || "",
    description: project?.description || "",
    image:       project?.image       || "",
    github:      project?.github      || "",
    demo:        project?.demo        || "",
    category:    project?.category    || "Web Development",
    featured:    project?.featured    || false,
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title) { show("Title is required", "error"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await authFetch(`${ENDPOINTS.projects}/${project.id}`, {
          method: "PATCH",
          body: JSON.stringify(form),
        });
        show("Project updated!");
      } else {
        await authFetch(ENDPOINTS.projects, {
          method: "POST",
          body: JSON.stringify(form),
        });
        show("Project created!");
      }
      setTimeout(() => { onSaved(); onClose(); }, 800);
    } catch (err) {
      show(err.message || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="adm-modal-backdrop" onClick={onClose}>
        <div className="adm-modal" onClick={(e) => e.stopPropagation()}>
          <div className="adm-modal-header">
            <h3 className="adm-modal-title">{isEdit ? "Edit Project" : "Add New Project"}</h3>
            <button className="adm-modal-close" onClick={onClose}>
              <i className="fa-solid fa-xmark"></i>
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="adm-modal-body">
              <div className="adm-form">

                <div className="adm-form-field">
                  <label className="adm-form-label">Title *</label>
                  <input name="title" className="adm-form-input" value={form.title} onChange={handleChange} placeholder="Project title" required />
                </div>

                <div className="adm-form-field">
                  <label className="adm-form-label">Description</label>
                  <textarea name="description" className="adm-form-textarea" value={form.description} onChange={handleChange} placeholder="Brief description of the project" />
                </div>

                <div className="adm-form-row">
                  <div className="adm-form-field">
                    <label className="adm-form-label">Category</label>
                    <select name="category" className="adm-form-select" value={form.category} onChange={handleChange}>
                      {["Web Development","Mobile Development","AI Solutions","Data Visualization","IoT"].map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div className="adm-form-field" style={{ justifyContent: "flex-end", paddingBottom: "0.2rem" }}>
                    <label className="adm-form-label">Featured</label>
                    <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", color: "rgba(255,255,255,0.6)", fontSize: "0.875rem" }}>
                      <input type="checkbox" name="featured" checked={form.featured} onChange={handleChange} style={{ accentColor: "#0a6b5a" }} />
                      Show on homepage
                    </label>
                  </div>
                </div>

                <div className="adm-form-field">
                  <label className="adm-form-label">Image URL</label>
                  <input name="image" className="adm-form-input" value={form.image} onChange={handleChange} placeholder="/images/project.png or https://..." />
                </div>

                <div className="adm-form-row">
                  <div className="adm-form-field">
                    <label className="adm-form-label">GitHub URL</label>
                    <input name="github" className="adm-form-input" value={form.github} onChange={handleChange} placeholder="https://github.com/..." />
                  </div>
                  <div className="adm-form-field">
                    <label className="adm-form-label">Live Demo URL</label>
                    <input name="demo" className="adm-form-input" value={form.demo} onChange={handleChange} placeholder="https://..." />
                  </div>
                </div>

              </div>
            </div>
            <div className="adm-modal-footer">
              <button type="button" className="adm-cancel-btn" onClick={onClose}>Cancel</button>
              <button type="submit" className="adm-save-btn" disabled={saving}>
                {saving ? <><span className="adm-loading-spinner" style={{width:14,height:14,borderWidth:2}}></span> Saving...</> : <><i className="fa-solid fa-check"></i> {isEdit ? "Update" : "Create"}</>}
              </button>
            </div>
          </form>
        </div>
      </div>
      {toast && (
        <div className={`adm-toast adm-toast--${toast.type}`}>
          <i className={`fa-solid ${toast.type === "success" ? "fa-check" : "fa-xmark"}`}></i>
          {toast.msg}
        </div>
      )}
    </>
  );
};

// ── Delete confirmation modal ────────────────────────────────────────────────
const DeleteConfirm = ({ project, onClose, onDeleted }) => {
  const [deleting, setDeleting] = useState(false);

  const confirm = async () => {
    setDeleting(true);
    try {
      await authFetch(`${ENDPOINTS.projects}/${project.id}`, { method: "DELETE" });
      onDeleted();
      onClose();
    } catch (err) {
      alert(err.message || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="adm-modal-backdrop" onClick={onClose}>
      <div className="adm-modal" style={{ maxWidth: 380 }} onClick={(e) => e.stopPropagation()}>
        <div className="adm-modal-header">
          <h3 className="adm-modal-title">Delete Project</h3>
          <button className="adm-modal-close" onClick={onClose}>
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div className="adm-modal-body">
          <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.9rem", lineHeight: 1.6 }}>
            Are you sure you want to delete <strong style={{ color: "white" }}>{project.title}</strong>? This action cannot be undone.
          </p>
        </div>
        <div className="adm-modal-footer">
          <button className="adm-cancel-btn" onClick={onClose}>Cancel</button>
          <button
            className="adm-save-btn"
            style={{ background: "linear-gradient(135deg,#b91c1c,#7f1d1d)" }}
            onClick={confirm}
            disabled={deleting}
          >
            {deleting ? "Deleting..." : <><i className="fa-solid fa-trash"></i> Delete</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Projects page ───────────────────────────────────────────────────────
const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState("");
  const [modal, setModal]       = useState(null); // null | { type: "add"|"edit"|"delete", project? }
  const { toast, show }         = useToast();

  const loadProjects = useCallback(async () => {
    try {
      const data = await authFetch(ENDPOINTS.projects);
      setProjects(data);
    } catch (err) {
      show(err.message || "Failed to load projects", "error");
    } finally {
      setLoading(false);
    }
  }, [show]);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  const filtered = projects.filter((p) =>
    p.title?.toLowerCase().includes(search.toLowerCase()) ||
    p.category?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      {/* Header */}
      <div className="adm-page-header">
        <div>
          <h1 className="adm-page-title">Projects</h1>
          <p className="adm-page-sub">{projects.length} projects total</p>
        </div>
        <button className="adm-add-btn" onClick={() => setModal({ type: "add" })}>
          <i className="fa-solid fa-plus"></i>
          Add Project
        </button>
      </div>

      {/* Search */}
      <div style={{ marginBottom: "1rem" }}>
        <div style={{ position: "relative", maxWidth: 320 }}>
          <i className="fa-solid fa-magnifying-glass" style={{
            position: "absolute", left: "0.875rem", top: "50%",
            transform: "translateY(-50%)", color: "rgba(255,255,255,0.25)", fontSize: "0.8rem"
          }}></i>
          <input
            className="adm-form-input"
            style={{ paddingLeft: "2.5rem", width: "100%" }}
            placeholder="Search projects..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="adm-table-wrap">
        {loading ? (
          <div className="adm-loading">
            <div className="adm-loading-spinner"></div>
            Loading projects...
          </div>
        ) : filtered.length === 0 ? (
          <div className="adm-empty">
            <i className="fa-solid fa-briefcase" style={{ fontSize: "2rem", color: "rgba(255,255,255,0.1)" }}></i>
            {search ? "No projects match your search." : "No projects yet. Add your first one!"}
          </div>
        ) : (
          <table className="adm-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Category</th>
                <th>Technologies</th>
                <th>Featured</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{p.title}</div>
                    {p.description && (
                      <div style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.35)", marginTop: 2 }}>
                        {p.description.substring(0, 60)}{p.description.length > 60 ? "..." : ""}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className="adm-badge adm-badge--green">{p.category || "—"}</span>
                  </td>
                  <td style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.8rem" }}>
                    {p.technologies?.slice(0, 3).join(", ") || "—"}
                    {p.technologies?.length > 3 ? ` +${p.technologies.length - 3}` : ""}
                  </td>
                  <td>
                    {p.featured
                      ? <span className="adm-badge adm-badge--gold">⭐ Featured</span>
                      : <span style={{ color: "rgba(255,255,255,0.2)", fontSize: "0.8rem" }}>—</span>
                    }
                  </td>
                  <td>
                    <div className="adm-action-btns">
                      <button className="adm-edit-btn" onClick={() => setModal({ type: "edit", project: p })}>
                        <i className="fa-solid fa-pen"></i> Edit
                      </button>
                      <button className="adm-delete-btn" onClick={() => setModal({ type: "delete", project: p })}>
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modals */}
      {modal?.type === "add" && (
        <ProjectModal
          project={null}
          onClose={() => setModal(null)}
          onSaved={() => { loadProjects(); show("Project created!"); }}
        />
      )}
      {modal?.type === "edit" && (
        <ProjectModal
          project={modal.project}
          onClose={() => setModal(null)}
          onSaved={() => { loadProjects(); show("Project updated!"); }}
        />
      )}
      {modal?.type === "delete" && (
        <DeleteConfirm
          project={modal.project}
          onClose={() => setModal(null)}
          onDeleted={() => { loadProjects(); show("Project deleted."); }}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className={`adm-toast adm-toast--${toast.type}`}>
          <i className={`fa-solid ${toast.type === "success" ? "fa-check" : "fa-xmark"}`}></i>
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default AdminProjects;
